import { JsonLd } from "@/components/JsonLd";
import type { Location } from "@/content/locations";
import type { Service } from "@/content/services";
import { SITE } from "@/content/site";
import { breadcrumbSchema } from "@/lib/seo";
import { faqSchema, cityLocalBusinessSchema } from "@/lib/schema";

type Faq = { q: string; a: string };

/** JSON-LD for a city × service page: LocalBusiness, Service, FAQ and breadcrumb. */
export function CityServiceStructuredData({
  loc,
  svc,
  angle,
  pillars,
  faqs,
  crumbs,
}: {
  loc: Location;
  svc: Service;
  angle: string;
  pillars: string[];
  faqs: Faq[];
  crumbs: { name: string; path: string }[];
}) {
  const path = `/seo-services/${loc.slug}/${svc.slug}`;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.url}${path}#service`,
    name: `${svc.title} in ${loc.city}`,
    serviceType: svc.title,
    description: angle,
    provider: { "@id": `${SITE.url}/#person` },
    areaServed: {
      "@type": "City",
      name: loc.city,
      address: {
        "@type": "PostalAddress",
        addressRegion: loc.region,
        addressCountry: loc.countryCode,
      },
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${svc.title} deliverables in ${loc.city}`,
      itemListElement: pillars.map((p) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: p },
      })),
    },
  };

  const localBusiness = cityLocalBusinessSchema({
    city: loc.city,
    region: loc.region,
    country: loc.country,
    countryCode: loc.countryCode,
    geo: loc.geo,
    path,
    name: `Freelancer Tamal — ${svc.title} in ${loc.city}`,
    description: angle,
    serviceType: [svc.title, `${svc.title} in ${loc.city}`],
  });

  return (
    <>
      <JsonLd data={localBusiness} />
      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema(faqs, loc.city)} />
      <JsonLd data={breadcrumbSchema(crumbs)} />
    </>
  );
}
